/**
 * Вкладка «Абоненты» раздела «Экология» панели команды (ecology-subscription, макет панели
 * администратора 16.09): клиенты на сопровождении — тариф, инженер, договор, ближайший выезд и
 * счётчик плана работ. Отсюда заполняется блок «Сопровождение» в кабинете клиента: карточка
 * (модалка «Абонент»), план работ (модалка «План работ») и завершение сопровождения.
 *
 * Завершённое сопровождение остаётся в списке историей (ответ заказчика 18.09: «периодически
 * возобновляем, хорошо бы, чтобы история подтянулась») — правка карточки возобновляет его вместе
 * с планом работ прошлых лет.
 *
 * Вкладку ведёт любой сотрудник Центрики (ответ заказчика 17.09) — в отличие от справочника,
 * вопросов и правил, которые правит только администратор.
 */
import { useCallback, useEffect, useState } from 'react'
import { Handshake, Pencil, Plus, Trash2 } from 'lucide-react'
import {
  teamEcologySubscriptionsApi,
  type EcoSubscriber,
  type EcoSubscription,
} from '@/lib/api'
import { getErrorMessage } from '@/lib/errors'
import { formatEcoDate } from '@/lib/ecology'
import { Panel } from '@/design-system/components/layout/panel'
import { Toolbar } from '@/design-system/components/layout/toolbar'
import { Button } from '@/design-system/components/primitives/button'
import { Badge } from '@/design-system/components/primitives/badge'
import { ConfirmDialog } from '@/design-system/components/primitives/confirm-dialog'
import { Alert } from '@/design-system/components/feedback/alert'
import { EmptyState } from '@/design-system/components/feedback/empty-state'
import {
  DataTable,
  DataTableBody,
  DataTableCell,
  DataTableHead,
  DataTableHeaderCell,
  DataTableRow,
} from '@/design-system/components/data/data-table'
import { SubscriberDialog } from './subscriber-dialog'
import { PlanDialog } from './plan-dialog'

type Editing = { clientId: string; clientName: string; subscription: EcoSubscription | null }

/** «18.09.2026» из ISO-времени завершения сопровождения. */
function endedLabel(iso: string): string {
  const d = new Date(iso)
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString('ru-RU')
}

export function SubscribersTab() {
  const [rows, setRows] = useState<EcoSubscriber[] | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [card, setCard] = useState<Editing | null>(null)
  const [plan, setPlan] = useState<(Editing & { subscription: EcoSubscription }) | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<EcoSubscriber | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback((signal?: AbortSignal) => {
    return teamEcologySubscriptionsApi
      .list(signal)
      .then((list) => {
        if (!signal?.aborted) setRows(list)
      })
      .catch((err) => {
        if (!signal?.aborted) setError(getErrorMessage(err, 'Не удалось загрузить абонентов'))
      })
  }, [])

  useEffect(() => {
    const controller = new AbortController()
    void load(controller.signal)
    return () => controller.abort()
  }, [load])

  /** Открывает модалку по строке: карточка и план грузятся вместе с планом работ. */
  async function open(row: EcoSubscriber, what: 'card' | 'plan') {
    setError(null)
    setBusy(true)
    try {
      const subscription = await teamEcologySubscriptionsApi.get(row.clientId)
      if (!subscription) {
        setError('Клиент уже не на сопровождении — обновите список')
        await load()
        return
      }
      const editing = { clientId: row.clientId, clientName: row.clientName, subscription }
      if (what === 'card') setCard(editing)
      else setPlan(editing)
    } catch (err) {
      setError(getErrorMessage(err, 'Не удалось открыть сопровождение'))
    } finally {
      setBusy(false)
    }
  }

  async function remove() {
    if (!confirmDelete) return
    setError(null)
    setBusy(true)
    try {
      await teamEcologySubscriptionsApi.remove(confirmDelete.clientId)
      setConfirmDelete(null)
      await load()
    } catch (err) {
      setConfirmDelete(null)
      setError(getErrorMessage(err, 'Не удалось снять клиента с сопровождения'))
    } finally {
      setBusy(false)
    }
  }

  const saved = () => {
    setCard(null)
    setPlan(null)
    void load()
  }

  return (
    <div>
      <p className="mb-3 text-13 text-txt-secondary">
        Блок «Сопровождение» в кабинете клиента заполняется здесь: тариф и договор, инженер,
        ближайший выезд и план работ на год с отметками «выполнено». Завершённое сопровождение
        остаётся в списке историей — правка карточки возобновляет его.
      </p>
      <Toolbar aria-label="Абоненты">
        <Button
          onClick={() => setCard({ clientId: '', clientName: '', subscription: null })}
          leftIcon={<Plus className="size-4 stroke-current" aria-hidden />}
        >
          Добавить абонента
        </Button>
      </Toolbar>

      {error ? (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      ) : null}

      <Panel>
        {rows === null ? (
          <p className="px-4 py-10 text-center text-txt-secondary">Загрузка…</p>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={Handshake}
            title="Абонентов нет"
            description="Добавьте клиента на сопровождение — он увидит блок «Сопровождение» в своём кабинете"
          />
        ) : (
          <div className="overflow-x-auto">
            <DataTable>
              <DataTableHead>
                <tr>
                  <DataTableHeaderCell>Клиент</DataTableHeaderCell>
                  <DataTableHeaderCell>Тариф</DataTableHeaderCell>
                  <DataTableHeaderCell>Инженер</DataTableHeaderCell>
                  <DataTableHeaderCell>Договор до</DataTableHeaderCell>
                  <DataTableHeaderCell>Ближайший выезд</DataTableHeaderCell>
                  <DataTableHeaderCell>План работ</DataTableHeaderCell>
                  <DataTableHeaderCell>{''}</DataTableHeaderCell>
                </tr>
              </DataTableHead>
              <DataTableBody>
                {rows.map((row) => (
                  <DataTableRow key={row.clientId}>
                    <DataTableCell className="min-w-56 font-medium text-txt">
                      {row.clientName}
                      {row.endedAt ? (
                        <span className="mt-0.5 block text-12 font-normal text-txt-tertiary">
                          сопровождение завершено {endedLabel(row.endedAt)}
                        </span>
                      ) : null}
                    </DataTableCell>
                    <DataTableCell>
                      <Badge variant={row.tariff === 'prime' ? 'blue' : 'gray'}>
                        {row.tariffLabel}
                      </Badge>
                    </DataTableCell>
                    <DataTableCell className="text-txt-secondary">
                      {row.engineerName || '—'}
                    </DataTableCell>
                    <DataTableCell className="text-txt-secondary">
                      {row.contractUntil ? formatEcoDate(row.contractUntil) : '—'}
                    </DataTableCell>
                    <DataTableCell className="text-txt-secondary">
                      {row.nextVisitOn ? formatEcoDate(row.nextVisitOn) : '—'}
                    </DataTableCell>
                    <DataTableCell>
                      <Badge
                        variant={
                          row.planTotal > 0 && row.planDone === row.planTotal ? 'green' : 'amber'
                        }
                      >
                        {row.planDone} из {row.planTotal}
                      </Badge>
                    </DataTableCell>
                    <DataTableCell className="text-right">
                      <div className="flex justify-end gap-1.5">
                        <Button
                          size="sm"
                          variant="secondary"
                          disabled={busy}
                          onClick={() => void open(row, 'plan')}
                        >
                          Открыть план
                        </Button>
                        <Button
                          size="sm"
                          variant="secondary"
                          title="Изменить сопровождение"
                          aria-label={`Изменить сопровождение клиента «${row.clientName}»`}
                          disabled={busy}
                          onClick={() => void open(row, 'card')}
                        >
                          <Pencil className="size-4 stroke-current" aria-hidden />
                        </Button>
                        {row.endedAt ? null : (
                          <Button
                            size="sm"
                            variant="secondary"
                            title="Завершить сопровождение"
                            aria-label={`Завершить сопровождение клиента «${row.clientName}»`}
                            disabled={busy}
                            onClick={() => setConfirmDelete(row)}
                          >
                            <Trash2 className="size-4 stroke-current" aria-hidden />
                          </Button>
                        )}
                      </div>
                    </DataTableCell>
                  </DataTableRow>
                ))}
              </DataTableBody>
            </DataTable>
          </div>
        )}
      </Panel>

      {card ? (
        <SubscriberDialog
          open
          clientId={card.clientId}
          clientName={card.clientName}
          subscription={card.subscription}
          onClose={() => setCard(null)}
          onSaved={saved}
        />
      ) : null}
      {plan ? (
        <PlanDialog
          open
          clientId={plan.clientId}
          clientName={plan.clientName}
          subscription={plan.subscription}
          onClose={() => setPlan(null)}
          onSaved={saved}
        />
      ) : null}
      <ConfirmDialog
        open={confirmDelete !== null}
        title="Завершение сопровождения"
        message={
          confirmDelete
            ? `Завершить сопровождение клиента «${confirmDelete.clientName}»? Блок «Сопровождение» пропадёт из его кабинета, а карточка и план работ останутся историей — при возобновлении они вернутся.`
            : ''
        }
        onClose={() => setConfirmDelete(null)}
        closeDisabled={busy}
        buttons={[
          {
            label: busy ? 'Завершение…' : 'Завершить',
            variant: 'danger',
            disabled: busy,
            onClick: () => void remove(),
          },
          {
            label: 'Отмена',
            variant: 'secondary',
            disabled: busy,
            onClick: () => setConfirmDelete(null),
          },
        ]}
      />
    </div>
  )
}
